import { api } from "@/services/api";

// ==================== Types ====================

export interface ProjectBudget {
  id: string;
  project_id: string;
  short_window_hours?: number;
  short_window_limit?: number | null;
  short_window_usage?: number;
  short_window_started_at?: string | null;
  long_window_days?: number;
  long_window_limit?: number | null;
  long_window_usage?: number;
  long_window_started_at?: string | null;
  alert_threshold_pct?: number;
  enforce_limit?: boolean | number;
  enforce_for_org_scope?: boolean | number;
  count_org_scope_usage?: boolean | number;
  currency?: string;
  short_window_alert_pct?: number | null;
  long_window_alert_pct?: number | null;
  budget_alert_roles?: string[];
  created_at?: string;
  updated_at?: string;
}

/** Budget status response (role-scoped) */
export interface BudgetWindowStatus {
  used_percentage: number;
  is_exceeded: boolean;
  resets_in_seconds: number | null;
  // Org-scope only fields
  limit?: number;
  used?: number;
  remaining?: number;
  started_at?: string | null;
  // Window size
  window_hours?: number;
  window_days?: number;
}

export interface BudgetStatus {
  short_window: BudgetWindowStatus | null;
  long_window: BudgetWindowStatus | null;
  enforce_limit: boolean;
  enforce_for_org_scope: boolean;
  count_org_scope_usage: boolean;
  is_blocked: boolean;
  currency?: string;
}

/** Budget breakdown response (composition of budget consumption by charge type / model) */
export interface BudgetBreakdownEntry {
  key: string;
  label: string;
  share_percentage: number;
  contribution_percentage: number;
}

export interface BudgetBreakdownItem extends BudgetBreakdownEntry {
  children: BudgetBreakdownEntry[];
}

export interface BudgetBreakdown {
  window: "short" | "long";
  window_hours: number | null;
  window_days: number | null;
  used_percentage: number;
  basis: "measured_share_projected_contribution";
  is_approximate: boolean;
  byok_present: boolean;
  items: BudgetBreakdownItem[];
}

export interface ProjectListItem {
  id: string;
  name: string;
  description?: string;
  status?: string;
  member_count: number;
  my_role?: string;
  budget?: {
    short_window_limit?: number | null;
    short_window_usage?: number;
    long_window_limit?: number | null;
    long_window_usage?: number;
  };
  /** AI-196: Custom appearance settings */
  settings?: {
    icon?: string;
    color?: string;
  };
  created_at?: string;
}

export interface ProjectDetails {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  status?: string;
  settings?: {
    default_model?: string;
    icon?: string;
    color?: string;
  };
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  budget?: ProjectBudget;
  stats?: {
    members_count: number;
  };
}

export interface Project {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  status?: string;
  settings?: {
    default_model?: string;
    icon?: string;
    color?: string;
  };
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ProjectMember {
  id: string;
  project_id: string;
  user_id: string;
  joined_at?: string;
  invited_by?: string;
  created_at?: string;
  updated_at?: string;
  membership_status?: "active" | "pending" | "suspended" | "removed";
  user?: {
    id: string;
    email: string;
    full_name: string;
    status?: string;
  };
  role?: {
    id: string;
    name: string;
    display_name: string;
    priority?: number;
  };
}

export interface CreateProjectInput {
  name: string;
  description?: string;
  settings?: {
    default_model?: string;
    icon?: string;
    color?: string;
  };
}

export interface UpdateProjectInput {
  name?: string;
  description?: string;
  settings?: {
    default_model?: string;
    icon?: string;
    color?: string;
  };
}

export interface AddProjectMemberInput {
  user_id: string;
}

export interface ConfigureProjectBudgetInput {
  short_window_hours?: number;
  short_window_limit?: number | null;
  long_window_days?: number;
  long_window_limit?: number | null;
  alert_threshold_pct?: number;
  enforce_limit?: boolean;
  enforce_for_org_scope?: boolean;
  count_org_scope_usage?: boolean;
  short_window_alert_pct?: number | null;
  long_window_alert_pct?: number | null;
  budget_alert_roles?: string[];
}

// ==================== API ====================

export const projectsApi = {
  // Project CRUD
  async list(): Promise<ProjectListItem[]> {
    const { data } = await api.get<ProjectListItem[]>("/projects");
    return data;
  },

  async getById(id: string): Promise<ProjectDetails> {
    const { data } = await api.get<ProjectDetails>(`/projects/${id}`);
    return data;
  },

  async create(body: CreateProjectInput): Promise<Project> {
    const { data } = await api.post<Project>("/projects", body);
    return data;
  },

  async update(id: string, body: UpdateProjectInput): Promise<void> {
    await api.patch(`/projects/${id}`, body);
  },

  async archive(id: string): Promise<void> {
    await api.delete(`/projects/${id}`);
  },

  async reactivate(id: string): Promise<void> {
    await api.post(`/projects/${id}/reactivate`);
  },

  // Project Members
  async getMembers(projectId: string): Promise<ProjectMember[]> {
    const { data } = await api.get<ProjectMember[]>(`/projects/${projectId}/members`);
    return data;
  },

  async addMember(projectId: string, body: AddProjectMemberInput): Promise<ProjectMember> {
    const { data } = await api.post<ProjectMember>(`/projects/${projectId}/members`, body);
    return data;
  },

  async removeMember(projectId: string, memberId: string): Promise<void> {
    await api.delete(`/projects/${projectId}/members/${memberId}`);
  },

  // Project Budget
  async getBudget(projectId: string): Promise<ProjectBudget | null> {
    const { data } = await api.get<ProjectBudget | null>(`/projects/${projectId}/budget`);
    return data;
  },

  async getBudgetStatus(projectId: string): Promise<BudgetStatus | null> {
    const { data } = await api.get<BudgetStatus | null>(`/projects/${projectId}/budget/status`);
    return data;
  },

  async getBudgetBreakdown(
    projectId: string,
    params: { window: "short" | "long"; limit?: number }
  ): Promise<BudgetBreakdown | null> {
    const { data } = await api.get<BudgetBreakdown | null>(
      `/projects/${projectId}/budget/breakdown`,
      { params }
    );
    return data;
  },

  async configureBudget(projectId: string, body: ConfigureProjectBudgetInput): Promise<ProjectBudget> {
    const { data } = await api.put<ProjectBudget>(`/projects/${projectId}/budget`, body);
    return data;
  },

  async removeBudget(projectId: string): Promise<void> {
    await api.delete(`/projects/${projectId}/budget`);
  },
};
